import { useEffect, useRef } from 'react';
import { useFetcher, useLoaderData } from 'react-router';

import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    NEXT_PROJECT_OPENING_API_ROUTE,
    type loader as nextOpeningLoader,
} from '~/routes/remix-api.project.next-opening';
import { OVERLAY_CAP } from '~/ui-rocco/pages/detail-shared/large-screen';
import { BANNER_PARALLAX_SCALE } from '~/ui-rocco/pages/detail-shared/banner-motion';
import {
    NEXT_LISTING_START_SCALE,
    useNextListingTurn,
} from '~/ui-rocco/pages/detail-shared/use-next-listing-turn';
import { nextTurnData } from '~/ui-rocco/pages/detail-shared/next-turn-event';
import {
    NextListingInvitation,
    nextInvitationOpacity,
} from '~/ui-rocco/pages/detail-shared/NextListingInvitation';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';

/**
 * The project title exactly as the page's own hero sets it, so the next
 * project's name is standing where its page will put it before that page
 * arrives. Any drift here is a jump the reader sees at the turn.
 */
const TITLE_TYPE = cn(
    'm-0 font-sans font-medium tracking-[-0.03em] text-[#e5e5e5]',
    'max-md:text-[2.3287rem] max-md:leading-[1.02]',
    'md:text-[5rem] md:leading-[4.25rem]',
);

/** Where the hero puts its title: the product page's 447px down, 40px in. */
const TITLE_PLACE = cn(
    'absolute max-md:right-6 max-md:bottom-6 max-md:left-6',
    'md:top-[27.9375rem] md:right-[3rem] md:bottom-auto md:left-[2.5rem]',
);

/** The hero's box: the taller project frame, capped at its design height. */
const HERO_BOX =
    'relative w-full max-md:aspect-[414/520] md:aspect-[1200/868] md:max-h-[86.8125rem]';

/** The hero's scrims, as it draws them over the photograph. */
const BOTTOM_SCRIM =
    'absolute inset-x-0 bottom-0 h-[36.875rem] bg-[linear-gradient(to_top,#181818_6.21%,rgba(24,24,24,0)_70.17%)]';
const TINT = 'absolute inset-0 bg-[#181818]/10';

/**
 * The breath above the invitation, on top of the section gap the page already
 * leaves before it: the product page's 360 in all, so the invitation arrives
 * as the page's ending rather than straight off the last grid.
 *
 * In viewport units, not rem, for the reason the product page's closing gives:
 * the rem height switches on at `md` while the root font-size holds its mobile
 * base until `lg`, so a rem gap runs to about twice the design between 768px
 * and 1023px. A tablet reads as desktop here, so that band is reachable.
 */
const EXTRA_BREATH = 180;
const EXTRA_BREATH_MOBILE = 80;

/** The next project's own title arrives from here, as the banner does. */
const TITLE_FADE_FROM = 0.7;

/**
 * The corner the banner starts on, as it is seen: a card's radius while it
 * is small, going to the hero's square corner as it grows. Written in the
 * box's own pixels, so it is divided by the scale the box is drawn at.
 */
const START_RADIUS_PX = 32;

const setOpacity = (node: HTMLElement | null, value: number) => {
    if (node) node.style.opacity = String(value);
};

/**
 * The foot of the project page: the professional's next project, growing as
 * the reader arrives at it, and then becoming that page.
 *
 * What grows is the next page's own opening — its banner at the hero's own
 * box, in the hero's own rendition, with its title where the hero puts it —
 * laid out at full size and scaled down from there. So by the time the banner
 * is full size, the reader is looking at the top of the next page, and the
 * turn is a swap of one document for an identical one.
 *
 * The turn itself — the warm on approach, the readiness gates, the settled
 * commit at the scroll clamp and the landing — is `useNextListingTurn`,
 * shared with the product page's turn. This section owns what the reader
 * sees: the banner, its squaring corner, and the title trading places with
 * the invitation.
 *
 * The section is not remounted between projects, so everything it holds is
 * re-seeded when the project under it changes.
 */
export const NextProjectTransition = () => {
    const { project, featuredProjects } = useLoaderData<typeof loader>();
    // The professional's next project. Their others in a fixed order, and
    // the one after this project in it — wrapping round at the end — so a
    // reader who keeps turning sees every project once rather than the same
    // two trading places.
    const others = (featuredProjects?.FeaturedProjects?.projects ?? [])
        .filter((item) => item.link && item.link !== project.Link)
        .sort((a, b) => a.id - b.id);
    const next = others.find((item) => item.id > project.ID) ?? others[0];
    const { log } = useTracker();

    const clipRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLDivElement>(null);

    // The next project's opening as its page paints it. Loaded on arrival, so
    // the banner (and the browser's cache of its rendition) is in place long
    // before the reader reaches it.
    const opening = useFetcher<typeof nextOpeningLoader>();
    const openingLoad = opening.load;
    const nextLink = next?.link;
    useEffect(() => {
        if (!nextLink) return;
        const params = new URLSearchParams({ link: nextLink });
        openingLoad(`${NEXT_PROJECT_OPENING_API_ROUTE}?${params}`);
    }, [openingLoad, nextLink]);

    // The hero's own rendition once it has answered for this project; until
    // then the listing's crop stands in.
    const answered =
        opening.data && opening.data.link === nextLink ? opening.data : null;
    const imageSrc = answered?.imageSrc ?? next?.images?.[0]?.src;
    const title = answered?.title ?? next?.title ?? '';

    const { sectionRef, frameRef } = useNextListingTurn({
        link: nextLink,
        answered: Boolean(answered),
        active: Boolean(next) && Boolean(imageSrc),
        anchorSelector: '[data-project-hero]',
        onCommit: () =>
            log('NextProjectTurn', {
                url: new URL(window.location.href),
                data: nextTurnData('project', nextLink ?? '', 'auto'),
            }),
        // The corner squares as the banner grows, and the next project's own
        // title arrives on it, as the invitation above goes.
        onProgress: (progress, scale) => {
            setOpacity(labelRef.current, nextInvitationOpacity(progress));
            if (clipRef.current) {
                const radius = (START_RADIUS_PX * (1 - progress)) / scale;
                clipRef.current.style.borderRadius = `${radius.toFixed(2)}px`;
            }
            setOpacity(
                titleRef.current,
                Math.min(
                    Math.max(
                        (progress - TITLE_FADE_FROM) / (1 - TITLE_FADE_FROM),
                        0,
                    ),
                    1,
                ),
            );
        },
    });

    if (!next || !imageSrc) return null;

    return (
        <>
            <SectionSpacer
                height={EXTRA_BREATH}
                mobileHeight={EXTRA_BREATH_MOBILE}
            />
            {/* Held at the middle of the screen before the turn, with the
                section riding up over it. Outside the section on purpose —
                the section is clipped to the next page's opening. */}
            <NextListingInvitation
                label="Next Project"
                fadeRef={labelRef}
                tone="dark"
            />
            {/* Exactly one viewport tall: the scroll clamp then stops with
                the banner's opening in place rather than past it, clipping it
                at the fold exactly as the next page's own fold will. */}
            <section
                ref={sectionRef}
                aria-label={`Next project: ${title}`}
                className="relative z-10 h-screen w-full overflow-hidden bg-[#181818]"
            >
                {/* A link, not only a scroll target. The growth is the invitation,
                but the next project has to be reachable by a click and by the
                keyboard whether or not the page ever turns itself. */}
                <RoccoLink
                    to={next.link}
                    // No link prefetch: Safari ignores the tag, so the scroll
                    // handler warms the route data with a plain fetch instead.
                    aria-label={`Go to ${title}`}
                    className="block no-underline"
                    onClick={() =>
                        log('NextProjectTurn', {
                            url: new URL(window.location.href),
                            data: nextTurnData('project', next.link, 'click'),
                        })
                    }
                >
                    {/* Laid out at the size it ends on and scaled down from there,
                    so growing costs one composited transform rather than a
                    re-layout of the banner. */}
                    <div
                        ref={frameRef}
                        style={{
                            transform: `scale(${NEXT_LISTING_START_SCALE})`,
                        }}
                        className="origin-top will-change-transform"
                    >
                        <div className={HERO_BOX}>
                            <div
                                ref={clipRef}
                                className="bg-neutral-900 absolute inset-0 overflow-hidden"
                            >
                                {/* At the parallax's resting scale, which is where
                                the hero holds its photograph from the first
                                paint. */}
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        transform: `scale(${BANNER_PARALLAX_SCALE})`,
                                    }}
                                >
                                    {/* The rendition as the hero paints it,
                                    by the same address, so the browser has
                                    the very file cached when the page turns.
                                    Fetched on arrival for the same reason. */}
                                    <img
                                        src={imageSrc}
                                        alt={title}
                                        loading="eager"
                                        decoding="async"
                                        className="absolute inset-0 size-full object-cover"
                                    />
                                </div>
                                <div className={TINT} />
                                <div className={BOTTOM_SCRIM} />
                            </div>
                            {/* The next page's own title, where its hero puts
                            it, arriving as the banner does. */}
                            <div className={OVERLAY_CAP}>
                                <div
                                    ref={titleRef}
                                    aria-hidden="true"
                                    style={{ opacity: 0 }}
                                    className={TITLE_PLACE}
                                >
                                    <p className={TITLE_TYPE}>{title}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </RoccoLink>
            </section>
        </>
    );
};
